import { useState, useMemo } from "react";
import {
  Plus, Pencil, Trash2, Wallet, CheckCircle2, Circle, Repeat, BellRing, RotateCcw, Radar,
  LayoutGrid, Zap, CreditCard, TrendingUp, AlertCircle,
} from "lucide-react";
import { useFinancialState } from "@/context/StateContext";
import { fmtMoney, fmtPct, uid, spentByCategory, billReminders, toMonthly, computeMetrics } from "@/lib/finance";
import { BUDGET_GROUPS, CATEGORY_PRESETS, GROUP_META } from "@/lib/budgetPresets";
import { RecurringBillsDialog } from "@/components/RecurringBillsDialog";
import { PageHeader, SectionCard, Money, EmptyState, AddButton, ProgressBar, KpiCard } from "@/components/shared";
import { FormModal, TextField, NumberField, SelectField } from "@/components/forms";
import { ScoreDial, BudgetVsActualBars, AllocationDonut } from "@/components/Charts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const FREQ = [
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly", label: "Yearly" },
];
const freqMonthly = (amount, frequency) =>
  frequency === "quarterly" ? Number(amount || 0) / 3 : toMonthly(amount, frequency);
const GROUP_OPTS = BUDGET_GROUPS.map((g) => ({ value: g, label: g }));

export default function Budget() {
  const { state, update, loading } = useFinancialState();
  const [bOpen, setBOpen] = useState(false);
  const [bForm, setBForm] = useState(null);
  const [billOpen, setBillOpen] = useState(false);
  const [billForm, setBillForm] = useState(null);
  const [recOpen, setRecOpen] = useState(false);
  const [presetOpen, setPresetOpen] = useState(false);

  const derived = useMemo(() => {
    if (!state) return null;
    const txns = state.transactions || [];
    const budgets = (state.budgets || []).map((b) => ({ ...b, group: b.group || "Other", spent: spentByCategory(txns, b.category) }));
    const budgetedCats = new Set(budgets.map((b) => b.category));
    // unbudgeted: expense categories with spend but no budget
    const spendByCat = {};
    txns.filter((t) => t.type === "expense").forEach((t) => {
      const c = t.category || "Other";
      spendByCat[c] = (spendByCat[c] || 0) + Math.abs(Number(t.amount || 0));
    });
    const unbudgeted = Object.entries(spendByCat)
      .filter(([c]) => !budgetedCats.has(c))
      .map(([category, spent]) => ({ category, spent }))
      .sort((a, b) => b.spent - a.spent);
    const spendDonut = Object.entries(spendByCat).map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value).slice(0, 7);
    return { budgets, unbudgeted, spendDonut };
  }, [state]);

  if (loading || !state || !derived) return <Skeleton className="h-96 rounded-[14px]" />;

  const { budgets, unbudgeted, spendDonut } = derived;
  const bills = state.bills || [];
  const subscriptions = bills.filter((b) => b.isSubscription);
  const plainBills = bills.filter((b) => !b.isSubscription);
  const m = computeMetrics(state);

  const totalBudget = budgets.reduce((s, b) => s + Number(b.budget || 0), 0);
  const totalSpent = budgets.reduce((s, b) => s + b.spent, 0);
  const remaining = totalBudget - totalSpent;
  const compliant = budgets.filter((b) => b.spent <= b.budget).length;
  const complianceScore = budgets.length ? Math.round((compliant / budgets.length) * 100) : 100;
  const monthlyBills = bills.reduce((s, b) => s + freqMonthly(b.amount, b.frequency || "monthly"), 0);
  const subsMonthly = subscriptions.reduce((s, b) => s + freqMonthly(b.amount, b.frequency || "monthly"), 0);
  const chartData = budgets.slice().sort((a, b) => b.budget - a.budget).slice(0, 8).map((b) => ({ category: b.category, budget: Math.round(b.budget), spent: Math.round(b.spent) }));

  // ---- budget CRUD ----
  const openBudget = (b) => { setBForm(b || { id: uid(), category: "", budget: "", group: "Essentials", rollover: false }); setBOpen(true); };
  const saveBudget = () => {
    if (!bForm.category) return toast.error("Category is required");
    const b = { ...bForm, budget: Number(bForm.budget || 0), group: bForm.group || "Other" };
    update((prev) => {
      const exists = (prev.budgets || []).some((x) => x.id === b.id);
      return { ...prev, budgets: exists ? prev.budgets.map((x) => (x.id === b.id ? b : x)) : [...(prev.budgets || []), b] };
    }, `Saved budget: ${b.category}`);
    setBOpen(false); toast.success("Budget saved");
  };
  const removeBudget = (id) => update((prev) => ({ ...prev, budgets: prev.budgets.filter((x) => x.id !== id) }), "Deleted budget");
  const addPreset = (category, group) => {
    if (budgets.some((b) => b.category.toLowerCase() === category.toLowerCase())) { toast.info(`${category} already budgeted`); return; }
    update((prev) => ({ ...prev, budgets: [...(prev.budgets || []), { id: uid(), category, budget: 0, group }] }), `Added budget: ${category}`);
    toast.success(`Added ${category} — set an amount`);
  };
  const addUnbudgeted = (category, spent) => {
    update((prev) => ({ ...prev, budgets: [...(prev.budgets || []), { id: uid(), category, budget: Math.ceil(spent / 10) * 10, group: "Other" }] }), `Budgeted ${category}`);
    toast.success(`Created a budget for ${category}`);
  };

  // ---- bill CRUD ----
  const openBill = (b, asSub = false) => { setBillForm(b || { id: uid(), name: "", amount: "", category: "", dueDay: 1, frequency: "monthly", isSubscription: asSub, autopay: false, account: "", paid: false }); setBillOpen(true); };
  const saveBill = () => {
    if (!billForm.name) return toast.error("Name is required");
    const b = { ...billForm, amount: Number(billForm.amount || 0), dueDay: Number(billForm.dueDay || 1) };
    update((prev) => {
      const exists = (prev.bills || []).some((x) => x.id === b.id);
      return { ...prev, bills: exists ? prev.bills.map((x) => (x.id === b.id ? b : x)) : [...(prev.bills || []), b] };
    }, `Saved bill: ${b.name}`);
    setBillOpen(false); toast.success("Saved");
  };
  const removeBill = (id) => update((prev) => ({ ...prev, bills: prev.bills.filter((x) => x.id !== id) }), "Deleted bill");
  const togglePaid = (id) => update((prev) => ({ ...prev, bills: prev.bills.map((x) => (x.id === id ? { ...x, paid: !x.paid } : x)) }), "Updated bill status");
  const resetPaid = () => update((prev) => ({ ...prev, bills: prev.bills.map((x) => ({ ...x, paid: false })) }), "Reset all bills to unpaid");
  const addRecurringBills = (newBills) => {
    if (!newBills.length) return;
    update((prev) => ({ ...prev, bills: [...(prev.bills || []), ...newBills] }), `Tracked ${newBills.length} recurring bill(s)`);
    toast.success(`Added ${newBills.length} recurring bill${newBills.length === 1 ? "" : "s"}`);
  };

  const { overdue, dueSoon } = billReminders(bills);
  const reminders = [...overdue, ...dueSoon];

  return (
    <div>
      <PageHeader title="Budget, bills & subscriptions" subtitle="Plan every category, track spending, and stay ahead of every payment." testid="budget-header"
        actions={<>
          <AddButton variant="secondary" onClick={() => setPresetOpen(true)} testid="add-preset-button"><LayoutGrid className="h-4 w-4 mr-1.5" />Preset categories</AddButton>
          <AddButton onClick={() => openBudget()} testid="add-budget-button"><Plus className="h-4 w-4 mr-1.5" />Add budget</AddButton>
        </>} />

      {/* KPI row */}
      <div className="grid grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        <KpiCard label="Budgeted" value={fmtMoney(totalBudget)} sub={m.income ? `${fmtPct((totalBudget / m.income) * 100, 0)} of income` : "Monthly plan"} icon={Wallet} testid="kpi-budgeted" />
        <KpiCard label="Spent" value={fmtMoney(totalSpent)} sub={`${budgets.length} categories`} testid="kpi-spent" />
        <KpiCard label="Remaining" value={fmtMoney(remaining)} sub={remaining >= 0 ? "On track" : "Over budget"} accent={remaining < 0} icon={TrendingUp} testid="kpi-remaining" />
        <KpiCard label="Monthly bills" value={fmtMoney(monthlyBills)} sub={`${bills.length} tracked`} icon={CreditCard} testid="kpi-monthly-bills" />
        <KpiCard label="Subscriptions" value={fmtMoney(subsMonthly)} sub={`${fmtMoney(subsMonthly * 12)}/yr`} icon={Repeat} testid="kpi-subscriptions" />
      </div>

      {reminders.length > 0 && (
        <div className="mb-4 rounded-[var(--radius-card)] border border-[color:rgba(198,139,60,0.35)] bg-[color:rgba(198,139,60,0.10)] p-4" data-testid="bill-reminders">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[color:rgba(198,139,60,0.20)] text-[var(--gold)] shrink-0"><BellRing className="h-5 w-5" /></span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[var(--text)]">
                {overdue.length > 0 ? `${overdue.length} bill${overdue.length > 1 ? "s" : ""} overdue` : ""}
                {overdue.length > 0 && dueSoon.length > 0 ? " · " : ""}
                {dueSoon.length > 0 ? `${dueSoon.length} due soon` : ""}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {reminders.map((b) => {
                  const isOver = overdue.includes(b);
                  return (
                    <button key={b.id} onClick={() => togglePaid(b.id)} data-testid={`reminder-${b.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[color:var(--border-subtle)] px-3 py-1 text-xs hover:border-[color:rgba(31,115,85,0.4)] transition-colors">
                      <span className={isOver ? "text-[var(--danger)]" : "text-[var(--text-muted)]"}>{isOver ? "Overdue" : `Day ${b.dueDay}`}</span>
                      <span className="font-medium text-[var(--text)]">{b.name}</span>
                      <span className="tabular-nums text-[var(--text-muted)]">{fmtMoney(b.amount)}</span>
                      <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" />
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[var(--text-faint)] mt-2">Tap a bill to mark it paid. Bills reset automatically at the start of each month.</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 space-y-4">
          {/* Grouped budgets */}
          <SectionCard title="Category budgets" subtitle={`${fmtMoney(totalSpent)} spent of ${fmtMoney(totalBudget)}`}
            right={<Button variant="ghost" size="sm" onClick={() => openBudget()} className="text-[var(--green)]" data-testid="budget-add-inline"><Plus className="h-4 w-4 mr-1" />Add</Button>} testid="budgets-card">
            {budgets.length ? (
              <div className="space-y-5">
                {BUDGET_GROUPS.map((g) => {
                  const items = budgets.filter((b) => b.group === g);
                  if (!items.length) return null;
                  const gBudget = items.reduce((s, b) => s + Number(b.budget || 0), 0);
                  const gSpent = items.reduce((s, b) => s + b.spent, 0);
                  return (
                    <div key={g} data-testid={`budget-group-${g}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text)]">{g}</span>
                          <span className="text-[11px] text-[var(--text-faint)]">{GROUP_META[g]?.hint}</span>
                        </div>
                        <span className="text-xs tabular-nums text-[var(--text-muted)]">{fmtMoney(gSpent)} / {fmtMoney(gBudget)}</span>
                      </div>
                      <div className="space-y-3">
                        {items.map((b) => {
                          const over = b.spent > b.budget && b.budget > 0;
                          const pct = b.budget ? Math.round((b.spent / b.budget) * 100) : 0;
                          return (
                            <div key={b.id} data-testid={`budget-row-${b.id}`} className="group">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-sm font-medium text-[var(--text)]">{b.category}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm tabular-nums ${over ? "text-[var(--danger)]" : "text-[var(--text-muted)]"}`}>{fmtMoney(b.spent)} / {fmtMoney(b.budget)}{b.budget ? ` · ${pct}%` : ""}</span>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 opacity-60 group-hover:opacity-100" onClick={() => openBudget(b)} data-testid={`edit-budget-${b.id}`} aria-label="Edit"><Pencil className="h-3 w-3" /></Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)] opacity-60 group-hover:opacity-100" onClick={() => removeBudget(b.id)} data-testid={`delete-budget-${b.id}`} aria-label="Delete"><Trash2 className="h-3 w-3" /></Button>
                                </div>
                              </div>
                              <ProgressBar value={b.spent} max={b.budget || 1} tone={over ? "danger" : pct > 85 ? "gold" : "green"} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <EmptyState icon={Wallet} title="No budgets set" message="Create category budgets or start from a preset library." action={<div className="flex gap-2"><AddButton onClick={() => setPresetOpen(true)}><LayoutGrid className="h-4 w-4 mr-1.5" />Presets</AddButton><AddButton variant="secondary" onClick={() => openBudget()}><Plus className="h-4 w-4 mr-1.5" />Add budget</AddButton></div>} />}
          </SectionCard>

          {chartData.length > 0 && (
            <SectionCard title="Budget vs actual" subtitle="Top categories" testid="budget-chart-card">
              <BudgetVsActualBars data={chartData} />
            </SectionCard>
          )}

          {unbudgeted.length > 0 && (
            <SectionCard title="Unbudgeted spending" subtitle="Categories you're spending in without a budget" testid="unbudgeted-card">
              <div className="space-y-2">
                {unbudgeted.slice(0, 6).map((u) => (
                  <div key={u.category} className="flex items-center justify-between rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2" data-testid={`unbudgeted-${u.category}`}>
                    <div className="flex items-center gap-2"><AlertCircle className="h-4 w-4 text-[var(--gold)]" /><span className="text-sm text-[var(--text)]">{u.category}</span></div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm tabular-nums text-[var(--text-muted)]">{fmtMoney(u.spent)}</span>
                      <Button variant="outline" size="sm" className="h-7 border-[color:var(--border-subtle)]" onClick={() => addUnbudgeted(u.category, u.spent)} data-testid={`budget-it-${u.category}`}><Plus className="h-3 w-3 mr-1" />Budget it</Button>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </div>

        <div className="lg:col-span-4 space-y-4">
          <SectionCard title="Budget compliance" subtitle="Categories within limit" testid="compliance-card">
            <div className="flex flex-col items-center py-2">
              <ScoreDial value={complianceScore} label="Score" />
              <p className="text-sm text-[var(--text-muted)] mt-3 text-center">{compliant} of {budgets.length || 0} categories within budget</p>
            </div>
          </SectionCard>
          {spendDonut.length > 0 && (
            <SectionCard title="Where money goes" subtitle="Spending by category" testid="spend-donut-card">
              <AllocationDonut data={spendDonut} height={230} />
            </SectionCard>
          )}
        </div>
      </div>

      {/* Bills & subscriptions */}
      <div className="mt-4">
        <Tabs defaultValue="bills">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <TabsList className="bg-[var(--surface-3)]">
              <TabsTrigger value="bills" className="data-[state=active]:bg-white data-[state=active]:text-[var(--green)]" data-testid="tab-bills"><CreditCard className="h-4 w-4 mr-1.5" />Bills ({plainBills.length})</TabsTrigger>
              <TabsTrigger value="subs" className="data-[state=active]:bg-white data-[state=active]:text-[var(--green)]" data-testid="tab-subscriptions"><Repeat className="h-4 w-4 mr-1.5" />Subscriptions ({subscriptions.length})</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={() => setRecOpen(true)} className="text-[var(--green)]" data-testid="detect-recurring-button"><Radar className="h-3.5 w-3.5 mr-1" />Find recurring</Button>
              {bills.length > 0 && <Button variant="ghost" size="sm" onClick={resetPaid} className="text-[var(--text-muted)]" data-testid="reset-bills-button"><RotateCcw className="h-3.5 w-3.5 mr-1" />Reset paid</Button>}
            </div>
          </div>

          <TabsContent value="bills">
            <SectionCard title="Bills" subtitle={`${fmtMoney(monthlyBills)}/mo across all payments`}
              right={<Button variant="ghost" size="sm" onClick={() => openBill(null, false)} className="text-[var(--green)]" data-testid="add-bill-button"><Plus className="h-4 w-4 mr-1" />Add bill</Button>} testid="bills-card">
              {plainBills.length ? <div className="space-y-2">{plainBills.map((b) => <BillRow key={b.id} b={b} onToggle={togglePaid} onEdit={() => openBill(b)} onRemove={removeBill} />)}</div>
                : <EmptyState icon={CreditCard} title="No bills yet" message="Add recurring bills like rent, utilities, and insurance." action={<AddButton onClick={() => openBill(null, false)}><Plus className="h-4 w-4 mr-1.5" />Add bill</AddButton>} />}
            </SectionCard>
          </TabsContent>

          <TabsContent value="subs">
            <SectionCard title="Subscriptions" subtitle={`${fmtMoney(subsMonthly)}/mo · ${fmtMoney(subsMonthly * 12)}/yr`}
              right={<Button variant="ghost" size="sm" onClick={() => openBill(null, true)} className="text-[var(--green)]" data-testid="add-subscription-button"><Plus className="h-4 w-4 mr-1" />Add subscription</Button>} testid="subscriptions-card">
              {subscriptions.length ? <div className="space-y-2">{subscriptions.map((b) => <BillRow key={b.id} b={b} onToggle={togglePaid} onEdit={() => openBill(b)} onRemove={removeBill} sub />)}</div>
                : <EmptyState icon={Repeat} title="No subscriptions" message="Track Netflix, Spotify, gym memberships and more — see the annual cost add up." action={<AddButton onClick={() => openBill(null, true)}><Plus className="h-4 w-4 mr-1.5" />Add subscription</AddButton>} />}
            </SectionCard>
          </TabsContent>
        </Tabs>
      </div>

      {/* Budget modal */}
      {bForm && (
        <FormModal open={bOpen} onOpenChange={setBOpen} title={budgets.some((x) => x.id === bForm.id) ? "Edit budget" : "Add budget"} onSubmit={saveBudget} testid="budget-modal">
          <TextField label="Category" value={bForm.category} onChange={(e) => setBForm({ ...bForm, category: e.target.value })} full testid="budget-category-input" hint="Match your transaction categories exactly" />
          <SelectField label="Group" value={bForm.group} onChange={(v) => setBForm({ ...bForm, group: v })} options={GROUP_OPTS} testid="budget-group-select" />
          <NumberField label="Monthly budget" value={bForm.budget} onChange={(e) => setBForm({ ...bForm, budget: e.target.value })} testid="budget-amount-input" />
        </FormModal>
      )}

      {/* Bill modal */}
      {billForm && (
        <FormModal open={billOpen} onOpenChange={setBillOpen} title={bills.some((x) => x.id === billForm.id) ? "Edit item" : billForm.isSubscription ? "Add subscription" : "Add bill"} onSubmit={saveBill} testid="bill-modal">
          <TextField label="Name" value={billForm.name} onChange={(e) => setBillForm({ ...billForm, name: e.target.value })} full testid="bill-name-input" />
          <NumberField label="Amount" value={billForm.amount} onChange={(e) => setBillForm({ ...billForm, amount: e.target.value })} testid="bill-amount-input" />
          <SelectField label="Frequency" value={billForm.frequency || "monthly"} onChange={(v) => setBillForm({ ...billForm, frequency: v })} options={FREQ} testid="bill-frequency-select" />
          <NumberField label="Due day of month" value={billForm.dueDay} onChange={(e) => setBillForm({ ...billForm, dueDay: e.target.value })} testid="bill-dueday-input" />
          <TextField label="Category" value={billForm.category} onChange={(e) => setBillForm({ ...billForm, category: e.target.value })} testid="bill-category-input" />
          <TextField label="Paid from (account)" value={billForm.account} onChange={(e) => setBillForm({ ...billForm, account: e.target.value })} placeholder="Checking, Rewards Card…" testid="bill-account-input" />
          <div className="sm:col-span-2 flex flex-wrap items-center gap-6 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={!!billForm.isSubscription} onCheckedChange={(v) => setBillForm({ ...billForm, isSubscription: v })} data-testid="bill-subscription-switch" />
              <span className="text-sm text-[var(--text-muted)]">Subscription</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch checked={!!billForm.autopay} onCheckedChange={(v) => setBillForm({ ...billForm, autopay: v })} data-testid="bill-autopay-switch" />
              <span className="text-sm text-[var(--text-muted)]">Autopay enabled</span>
            </label>
          </div>
        </FormModal>
      )}

      {/* Preset library */}
      <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
        <DialogContent className="bg-[var(--surface-2)] border border-[color:var(--border-subtle)] max-w-lg" data-testid="preset-modal">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-xl text-[var(--text)]">Preset categories</DialogTitle>
            <DialogDescription className="text-[var(--text-muted)]">Tap any category to add it — then set its amount.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-auto">
            {CATEGORY_PRESETS.map((grp) => (
              <div key={grp.group}>
                <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2">{grp.group}</div>
                <div className="flex flex-wrap gap-2">
                  {grp.categories.map((c) => {
                    const exists = budgets.some((b) => b.category.toLowerCase() === c.toLowerCase());
                    return (
                      <button key={c} disabled={exists} onClick={() => addPreset(c, grp.group)} data-testid={`preset-${c}`}
                        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm transition-colors ${exists ? "border-[color:var(--border-subtle)] bg-[var(--surface-3)] text-[var(--text-faint)] cursor-not-allowed" : "border-[color:var(--border-subtle)] bg-white text-[var(--text)] hover:border-[color:rgba(31,115,85,0.4)] hover:bg-[color:rgba(31,115,85,0.06)]"}`}>
                        {exists ? <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" /> : <Plus className="h-3.5 w-3.5" />}{c}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <RecurringBillsDialog open={recOpen} onOpenChange={setRecOpen} transactions={state.transactions || []} bills={bills} onAdd={addRecurringBills} />
    </div>
  );
}

function BillRow({ b, onToggle, onEdit, onRemove, sub }) {
  const monthly = b.frequency && b.frequency !== "monthly";
  return (
    <div className="flex items-center justify-between rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`bill-row-${b.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <button onClick={() => onToggle(b.id)} data-testid={`toggle-bill-${b.id}`} aria-label="Toggle paid" className={b.paid ? "text-[var(--green)]" : "text-[var(--text-faint)]"}>
          {b.paid ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
        </button>
        <div className="min-w-0">
          <div className="text-sm font-medium text-[var(--text)] flex items-center gap-1.5 flex-wrap">
            {b.name}
            {sub && <Repeat className="h-3 w-3 text-[var(--gold)]" />}
            {b.autopay && <span className="inline-flex items-center gap-0.5 rounded-full bg-[color:rgba(31,115,85,0.12)] text-[var(--green)] px-1.5 py-0.5 text-[10px] font-medium"><Zap className="h-2.5 w-2.5" />Auto</span>}
          </div>
          <div className="text-[11px] text-[var(--text-faint)] flex flex-wrap gap-x-2">
            <span>Due day {b.dueDay}</span>
            {monthly && <span className="capitalize">· {b.frequency}</span>}
            {b.category && <span>· {b.category}</span>}
            {b.account && <span>· {b.account}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <div className="text-right mr-1">
          <div className="text-sm tabular-nums text-[var(--text)]">{fmtMoney(b.amount)}</div>
          {monthly && <div className="text-[10px] text-[var(--text-faint)]">≈{fmtMoney(freqMonthly(b.amount, b.frequency))}/mo</div>}
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onEdit} aria-label="Edit"><Pencil className="h-3 w-3" /></Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--danger)]" onClick={() => onRemove(b.id)} aria-label="Delete"><Trash2 className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}
